import { useCallback, useRef, useState } from 'react';
import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { SupabaseClient } from '../supabase/client';
import { listStockWorklist, StockWorklistError } from '../services/stock.service';
import type {
  StockWorklistCursor,
  StockWorklistFilter,
  StockWorklistPage,
  StockWorklistRow,
  StockWorklistScope,
} from '../types/stock';

export interface UseStockWorklistOptions {
  readonly storeId: string | null;
  readonly userId: string | null;
  readonly scope: StockWorklistScope;
  readonly filter: StockWorklistFilter;
  readonly query: string;
  readonly pageSize?: number;
}

export interface UseStockWorklistResult {
  readonly rows: readonly StockWorklistRow[];
  readonly page: StockWorklistPage | null;
  readonly isLoading: boolean;
  readonly isRefreshing: boolean;
  readonly isLoadingMore: boolean;
  readonly initialError: StockWorklistError | null;
  readonly refreshError: StockWorklistError | null;
  readonly loadMoreError: StockWorklistError | null;
  readonly accessDenied: boolean;
  readonly hasMore: boolean;
  readonly needsRefresh: boolean;
  readonly automaticRefreshPaused: boolean;
  readonly loadMore: () => void;
  readonly refresh: () => void;
  readonly retryLoadMore: () => void;
}

export const stockWorklistKeys = {
  store: (storeId: string | null) => ['stock-summary', storeId] as const,
  list: (options: UseStockWorklistOptions) =>
    [
      'stock-summary',
      options.storeId,
      'f155',
      options.userId,
      options.scope,
      options.filter,
      options.query.trim(),
      options.pageSize ?? 50,
    ] as const,
};

type WorklistData = InfiniteData<StockWorklistPage, StockWorklistCursor | null>;

export function useStockWorklist(
  client: SupabaseClient,
  options: UseStockWorklistOptions,
): UseStockWorklistResult {
  const queryClient = useQueryClient();
  const { storeId, userId, scope, filter, pageSize = 50 } = options;
  const search = options.query.trim();
  const identity = JSON.stringify([storeId, userId]);
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const [deniedIdentity, setDeniedIdentity] = useState<string | null>(null);
  const [restarting, setRestarting] = useState(false);
  const restartLock = useRef(false);
  const pageLock = useRef(false);
  const accessDenied = deniedIdentity === identity;
  const enabled = Boolean(storeId && userId) && !accessDenied;
  const queryKey = stockWorklistKeys.list(options);

  const query = useInfiniteQuery({
    queryKey,
    enabled,
    initialPageParam: null as StockWorklistCursor | null,
    queryFn: async ({ pageParam, signal }): Promise<StockWorklistPage> => {
      if (!storeId || !userId) throw new StockWorklistError('access_denied');
      try {
        return await listStockWorklist(
          client,
          {
            storeId,
            scope,
            filter,
            query: search,
            limit: pageSize,
            cursor: pageParam,
          },
          { signal },
        );
      } catch (error) {
        if (error instanceof StockWorklistError && error.code === 'access_denied') {
          // Latch before cancellation: TanStack cancellation may restore cached success.
          if (currentIdentity.current === identity) setDeniedIdentity(identity);
          void queryClient.cancelQueries(
            { queryKey: [...stockWorklistKeys.store(storeId), 'f155'] },
            { revert: false },
          );
          queryClient.removeQueries({ queryKey: [...stockWorklistKeys.store(storeId), 'f155'] });
          void queryClient.invalidateQueries({ queryKey: ['admin', 'access', userId] });
        }
        throw error;
      }
    },
    getNextPageParam: (last) => last.nextCursor,
    retry: (failures, error) =>
      error instanceof StockWorklistError && error.code === 'network' && failures < 1,
    gcTime: 5 * 60_000,
    staleTime: 0,
    refetchInterval: (current) => ((current.state.data?.pages.length ?? 0) > 1 ? false : 60_000),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: (current) => (current.state.data?.pages.length ?? 0) <= 1,
    refetchOnMount: (current) => (current.state.data?.pages.length ?? 0) <= 1,
  });

  const pages = enabled ? (query.data?.pages ?? []) : [];
  const page = pages[0] ?? null;
  const seen = new Set<string>();
  const rows: StockWorklistRow[] = [];
  let needsRefresh = false;
  let emptyContinuation = false;
  for (const [index, current] of pages.entries()) {
    if (
      page &&
      (current.matchingCount !== page.matchingCount ||
        current.catalogVariantCount !== page.catalogVariantCount ||
        current.scopeCounts.variants !== page.scopeCounts.variants ||
        current.scopeCounts.outOfStock !== page.scopeCounts.outOfStock ||
        current.scopeCounts.lowStock !== page.scopeCounts.lowStock)
    )
      needsRefresh = true;
    if (index > 0 && current.items.length === 0) emptyContinuation = true;
    for (const row of current.items) {
      if (seen.has(row.variantId)) needsRefresh = true;
      else {
        seen.add(row.variantId);
        rows.push(row);
      }
    }
  }
  const last = pages.at(-1);
  if (
    page &&
    (emptyContinuation || (last?.nextCursor === null && rows.length !== page.matchingCount))
  )
    needsRefresh = true;
  const hasMore = enabled && Boolean(last?.nextCursor) && !emptyContinuation;
  const error =
    query.error instanceof StockWorklistError
      ? query.error
      : query.error
        ? new StockWorklistError('unknown')
        : null;

  const loadMore = useCallback((): void => {
    if (!hasMore || query.isFetching || restartLock.current || pageLock.current) return;
    pageLock.current = true;
    void query.fetchNextPage({ cancelRefetch: false }).finally(() => {
      pageLock.current = false;
    });
  }, [hasMore, query]);

  const refresh = useCallback((): void => {
    if (
      !storeId ||
      !userId ||
      restartLock.current ||
      (query.isFetching && !query.isFetchingNextPage)
    )
      return;
    restartLock.current = true;
    setRestarting(true);
    void (async (): Promise<void> => {
      try {
        await queryClient.cancelQueries({ queryKey, exact: true });
        if (currentIdentity.current !== identity) return;
        if (accessDenied) {
          queryClient.removeQueries({ queryKey, exact: true });
          setDeniedIdentity(null);
          return;
        }
        queryClient.setQueryData<WorklistData>(queryKey, (data) =>
          data
            ? {
                pages: data.pages.slice(0, 1),
                pageParams: [null],
              }
            : undefined,
        );
        await query.refetch();
      } finally {
        restartLock.current = false;
        setRestarting(false);
      }
    })();
  }, [storeId, userId, query, queryClient, queryKey, identity, accessDenied]);

  return {
    rows,
    page,
    isLoading: enabled && query.isPending,
    isRefreshing:
      enabled &&
      (restarting || (query.isFetching && !query.isFetchingNextPage && !query.isPending)),
    isLoadingMore: enabled && query.isFetchingNextPage,
    initialError: enabled && !page ? error : null,
    refreshError: enabled && page && !query.isFetchNextPageError ? error : null,
    loadMoreError: enabled && page && query.isFetchNextPageError ? error : null,
    accessDenied,
    hasMore,
    needsRefresh,
    automaticRefreshPaused: pages.length > 1,
    loadMore,
    refresh,
    retryLoadMore: loadMore,
  };
}
